import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Progress } from '../components/ui/progress';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { assessmentAPI } from '../lib/api';
import { toast } from 'sonner';

const QUESTIONS = [
  {
    id: 'industry',
    label: 'What industry is your business in?',
    type: 'select',
    options: ['E-commerce', 'Professional Services', 'Manufacturing', 'SaaS/Technology', 'Healthcare', 'Finance', 'Retail', 'Other']
  },
  {
    id: 'employees',
    label: 'How many employees do you have?',
    type: 'number',
    placeholder: '25'
  },
  {
    id: 'revenue_range',
    label: 'What is your annual revenue range?',
    type: 'radio',
    options: ['<$500K', '$500K-$1M', '$1M-$5M', '$5M-$10M', '$10M-$50M', '>$50M']
  },
  {
    id: 'primary_challenge',
    label: 'What is your primary operational challenge?',
    type: 'radio',
    options: ['Sales', 'Recruiting', 'Operations', 'Data Analytics', 'Customer Service']
  },
  {
    id: 'avg_hourly_cost',
    label: 'Average hourly cost per employee (including overhead)?',
    type: 'number',
    placeholder: '35',
    prefix: '$'
  },
  {
    id: 'manual_processes',
    label: 'What manual processes take up the most time?',
    type: 'text',
    placeholder: 'e.g., Data entry, scheduling, reporting...'
  },
  {
    id: 'monthly_volume',
    label: 'How many tasks/transactions do you process monthly?',
    type: 'number',
    placeholder: '2000'
  },
  {
    id: 'error_rate',
    label: 'What is your estimated error/rework rate (%)?',
    type: 'number',
    placeholder: '8',
    suffix: '%'
  },
  {
    id: 'growth_target',
    label: 'What is your growth target for the next 12 months (%)?',
    type: 'number',
    placeholder: '50',
    suffix: '%'
  }
];

export default function AssessmentTool() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  
  const currentQuestion = QUESTIONS[step];
  const progress = ((step + 1) / QUESTIONS.length) * 100;
  
  const handleNext = () => {
    if (!formData[currentQuestion.id]) {
      toast.error('Please answer the question to continue');
      return;
    }
    
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      submitAssessment();
    }
  };
  
  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };
  
  const updateFormData = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };
  
  const submitAssessment = async () => {
    setLoading(true);
    try {
      const assessmentData = {
        industry: formData.industry || '',
        employees: parseInt(formData.employees) || 0,
        revenue_range: formData.revenue_range || '',
        primary_challenge: formData.primary_challenge || '',
        avg_hourly_cost: parseFloat(formData.avg_hourly_cost) || 0,
        manual_processes: formData.manual_processes || '',
        monthly_volume: parseInt(formData.monthly_volume) || 0,
        error_rate: parseFloat(formData.error_rate) || 0,
        growth_target: parseFloat(formData.growth_target) || 0
      };
      
      const result = await assessmentAPI.submit(assessmentData);
      toast.success('Assessment completed!');
      navigate(`/assessment/results/${result.id}`);
    } catch (error) {
      console.error('Assessment error:', error);
      toast.error('Failed to submit assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow bg-muted/30 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <Badge className="mb-4" variant="secondary">Savings Calculator</Badge>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              How Much Can Automation Save You?
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Answer {QUESTIONS.length} quick questions to discover your automation potential and projected cost savings.
            </p>
          </div>
          
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Progress</span>
              <span className="text-sm font-medium">{step + 1} of {QUESTIONS.length}</span>
            </div>
            <Progress value={progress} data-testid="assessment-progress-bar" className="h-2" />
          </div>
          
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-xl">Question {step + 1}</CardTitle>
              <CardDescription>{currentQuestion.label}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {currentQuestion.type === 'select' && (
                <Select value={formData[currentQuestion.id]} onValueChange={(value) => updateFormData(currentQuestion.id, value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentQuestion.options.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {currentQuestion.type === 'radio' && (
                <RadioGroup value={formData[currentQuestion.id]} onValueChange={(value) => updateFormData(currentQuestion.id, value)}>
                  {currentQuestion.options.map(option => (
                    <div key={option} className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer">
                      <RadioGroupItem value={option} id={option} />
                      <Label htmlFor={option} className="flex-grow cursor-pointer">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
              
              {currentQuestion.type === 'number' && (
                <div>
                  <div className="relative">
                    {currentQuestion.prefix && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{currentQuestion.prefix}</span>
                    )}
                    <Input
                      type="number"
                      placeholder={currentQuestion.placeholder}
                      value={formData[currentQuestion.id] || ''}
                      onChange={(e) => updateFormData(currentQuestion.id, e.target.value)}
                      className={currentQuestion.prefix ? 'pl-8' : ''}
                    />
                    {currentQuestion.suffix && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{currentQuestion.suffix}</span>
                    )}
                  </div>
                </div>
              )}
              
              {currentQuestion.type === 'text' && (
                <Input
                  type="text"
                  placeholder={currentQuestion.placeholder}
                  value={formData[currentQuestion.id] || ''}
                  onChange={(e) => updateFormData(currentQuestion.id, e.target.value)}
                />
              )}
              
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={step === 0 || loading}
                  data-testid="assessment-back-button"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                
                <Button
                  onClick={handleNext}
                  disabled={loading}
                  data-testid="assessment-next-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : step === QUESTIONS.length - 1 ? (
                    'Get Results'
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card className="rounded-xl mt-6 bg-muted/50">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground text-center">
                Your data is secure and will only be used to generate your personalized automation report.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}